import React, { useEffect, useRef, useState } from "react";
import { Button, Center, Flex, Grid, GridItem, Heading, Image, Input, Table, TableCaption, TableContainer, Tbody, Td, Text, Th, Thead, Tooltip, Tr, VStack, useDisclosure } from "@chakra-ui/react";
import { changeStatusToAccepted, getType, isEmployee } from "../../components/common/CommonFunctions";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import ToastMessage from "../../components/utility/ToastMessage";
import CustomSpinner from "../../components/utility/CustomSpinner";
import UserInfo from "../../components/adminEnd/UserInfo";
import EmployeeInfo from "../../components/adminEnd/EmployeeInfo";
import StudentInfo from "../../components/adminEnd/StudentInfo";
import UserInfoStyle from '../../assets/css/modular/user-info.module.css';
import EmployeeIdCardWithValue from "../../components/id-card/EmployeeIdCardWithValue";
import HonsMastersStudentIdCardWithValue from "../../components/id-card/HonsMastersStudentIdCardWithValue";
import MphilPhdStudentIdCardWithValue from "../../components/id-card/MphilPhdStudentIdCardWithValue";
import { useReactToPrint } from "react-to-print";
import ConfirmModal from "../../components/common/ConfirmModal";
import UpdateValidityModal from "../../components/common/UpdateValidityModal";
import EveningCourseStudentIdCardWithValue from "../../components/id-card/EveningCourseStudentIdCardWithValue";
import UpdateRfidCardNo from "../../components/common/UpdateRfidCardNo";

export default function UserApplyDetails() {
    const token = localStorage.getItem('my_token');
    const { user_id, apply_id } = useParams();
    const [userInfo, setUserInfo] = useState({});
    const [employeeInfo, setEmployeeInfo] = useState({});
    const [studentInfo, setStudentInfo] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [refresh, setRefresh] = useState(false);

    useEffect(() => {
        fetchUser();
    }, [user_id, refresh]);

    const fetchUser = async () => {
        setIsLoading(true);

        const config = {
            headers: { Authorization: `Bearer ${token}` }
        };

        try {
            var apiResponse = (await axios.get(`${process.env.REACT_APP_API_URL}/api/user_detailed_info/${user_id}`, config)).data;
            if (apiResponse.error === false && apiResponse.results) {
                const { user_info, employee_info, student_info } = apiResponse?.results;
                setUserInfo(user_info);
                setEmployeeInfo(employee_info);
                setStudentInfo(student_info);
            }
        } catch (error) {
            ToastMessage('error', 'Something went wrong');
        }

        setIsLoading(false);
    }

    return (
        isLoading
            ? <CustomSpinner />
            :
            <Details
                userInfo={userInfo}
                employeeInfo={employeeInfo}
                studentInfo={studentInfo}
                apply_id={apply_id}
                setRefresh={setRefresh}
            />
    );
}

const Details = ({ userInfo, employeeInfo, studentInfo, apply_id, setRefresh }) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const IdCard = () => {
        const token = localStorage.getItem('my_token');
        const [thumb, setThumb] = useState('');
        const [showModal, setShowModal] = useState(false);
        const [showHeader, setShowHeader] = useState(true);
        const [showUpdateValidityModal, setShowUpdateValidityModal] = useState(false);
        const [tempValidity, setTempValidity] = useState(() => {
            if (userInfo?.user_type === '5') {
                return studentInfo?.mphil_phd_details?.validity;
            } else if (userInfo?.user_type === '8') {
                return studentInfo?.evening_course_student_details?.validity;
            } else if (userInfo?.user_type === '4') {
                return studentInfo?.hons_masters_details?.validity;
            } else {
                return '';
            }
        });

        const navigate = useNavigate();
        const componentRef = useRef();

        useEffect(() => {
            if (userInfo?.thumb) {
                getThumb(userInfo?.thumb)
            }
        }, [userInfo?.thumb]);

        const handlePrint = useReactToPrint({
            content: () => componentRef.current,
            onAfterPrint: () => {
                const redirectTo = () => {
                    let navigateTo = -1;
                    if (userInfo?.user_type === '3') {
                        navigateTo = '/applications/teachers';
                    } else if (userInfo?.user_type === '4') {
                        navigateTo = '/applications/hons-masters-students';
                    } else if (userInfo?.user_type === '5') {
                        navigateTo = '/applications/mphil-phd-students';
                    } else if (userInfo?.user_type === '6') {
                        navigateTo = '/applications/officers';
                    } else if (userInfo?.user_type === '7') {
                        navigateTo = '/applications/staffs';
                    } else if (userInfo?.user_type === '8') {
                        navigateTo = '/applications/evening-course-students';
                    }

                    navigate(navigateTo);
                }

                changeStatusToAccepted(token, userInfo?.id, apply_id);
            }
        });

        const handleBeforePrint = () => {
            setShowModal(true);
        }

        const handlePreview = useReactToPrint({
            content: () => componentRef.current
        });

        const getThumb = async (param) => {
            const config = {
                headers: { Authorization: `Bearer ${token}` },
                responseType: "blob",
                params: {
                    filename: param
                }
            };

            try {
                var response = (await axios.get(`${process.env.REACT_APP_API_URL}/api/get-image-from-path`, config));
                if (response.status == 200) {
                    const data = response.data;
                    const blob = new Blob([data], { type: data.type });
                    const url = URL.createObjectURL(blob);
                    setThumb(url);
                }
            } catch ({ response }) {
                if (response?.status == 404) {
                    ToastMessage('warning', 'Image not found');
                } else {
                    ToastMessage('error', 'Something went wrong');
                }
            }
        }

        const updateValidity = () => {
            setShowUpdateValidityModal(true);
        }

        const performValidityChange = (validity) => {
            let config = {
                headers: {
                    Accept: 'application/json',
                    Authorization: `Bearer ${token}`
                }
            };

            const params = {
                validity: validity,
                user_type: userInfo?.user_type
            }

            axios
                .post(`${process.env.REACT_APP_API_URL}/api/update-validity/${userInfo?.id}`, params, config)
                .then((response) => {
                    setTempValidity(validity);
                    ToastMessage('success', 'Successfully Updated');
                })
                .catch((error) => {
                    const { status } = error?.response;

                    let message = 'Something went wrong';
                    if (status == 422) {
                        message = 'Invalid Input';
                    }

                    if (status == 400) {
                        message = 'Data already exists';
                    }

                    ToastMessage('error', message);
                });
        }

        return (
            <Flex direction={'column'} gap={'10px'}>
                {userInfo?.is_accepted == '0' &&
                    <>
                        <Tooltip label='Accept this application. This will change the status of the application. So be careful. If you want to see how this looks the press "Preview Id Card" button'>
                            <Button colorScheme='teal' size='sm' onClick={handleBeforePrint}>
                                <span>Accept application and PRINT</span>
                            </Button>
                        </Tooltip>
                    </>
                }

                {showModal &&
                    <ConfirmModal
                        reset={setShowModal}
                        title={'PRINT?'}
                        desc={'If you click the [YES] button application will be accepted either you print it or not. Are you sure?'}
                        action={handlePrint}
                    />
                }

                {showUpdateValidityModal &&
                    <UpdateValidityModal
                        reset={setShowUpdateValidityModal}
                        action={performValidityChange}
                        validity={tempValidity}
                    />
                }

                <div ref={componentRef}>
                    {isEmployee(userInfo?.user_type) &&
                        <EmployeeIdCardWithValue
                            office={employeeInfo?.employee_details?.office}
                            thumb={thumb}
                            fullname={userInfo?.fullname}
                            employee_id={employeeInfo?.employee_details?.employee_id}
                            nid={userInfo?.nid}
                            blood_group={userInfo?.blood_group}
                            designation={employeeInfo?.employee_details?.designation}
                            dob={userInfo?.dob}
                            showHeader={showHeader}
                        />
                    }
                    {userInfo?.user_type == '4' &&
                        <HonsMastersStudentIdCardWithValue
                            hall={studentInfo?.hons_masters_details?.hall}
                            thumb={thumb}
                            api_thumb={userInfo?.api_thumb}
                            fullname={userInfo?.fullname}
                            registration_no={studentInfo?.hons_masters_details?.registration_no}
                            department={studentInfo?.hons_masters_details?.dept_or_inst == '0' ? studentInfo?.hons_masters_details?.department : studentInfo?.hons_masters_details?.institute}
                            blood_group={userInfo?.blood_group}
                            batch={studentInfo?.hons_masters_details?.batch}
                            validity={tempValidity}
                            showHeader={showHeader}
                        />
                    }
                    {userInfo?.user_type == '5' &&
                        <MphilPhdStudentIdCardWithValue
                            hall={studentInfo?.mphil_phd_details?.hall}
                            thumb={thumb}
                            fullname={userInfo?.fullname}
                            registration_no={studentInfo?.mphil_phd_details?.registration_no}
                            department={studentInfo?.mphil_phd_details?.dept_or_inst == '0' ? studentInfo?.mphil_phd_details?.department : studentInfo?.mphil_phd_details?.institute}
                            blood_group={userInfo?.blood_group}
                            session={studentInfo?.mphil_phd_details?.session}
                            regularity={studentInfo?.mphil_phd_details?.type == '0' ? 'Regular' : 'Irregular'}
                            apply_for={studentInfo?.mphil_phd_details?.mphill_or_phd == '0' ? 'Mphil' : studentInfo?.mphil_phd_details?.mphill_or_phd == '1' ? 'Phd' : ''}
                            validity={tempValidity}
                            showHeader={showHeader}
                        />
                    }
                    {userInfo?.user_type == '8' &&
                        <EveningCourseStudentIdCardWithValue
                            hall={studentInfo?.evening_course_student_details?.hall}
                            thumb={thumb}
                            fullname={userInfo?.fullname}
                            registration_no={studentInfo?.evening_course_student_details?.registration_no}
                            blood_group={userInfo?.blood_group}
                            session={studentInfo?.evening_course_student_details?.session}
                            batch={studentInfo?.evening_course_student_details?.batch}
                            admission_year={studentInfo?.evening_course_student_details?.admission_year}
                            validity={tempValidity}
                            course_title={studentInfo?.evening_course_student_details?.course_title}
                            course_id={studentInfo?.evening_course_student_details?.course_id}
                            showHeader={showHeader}
                        />
                    }
                </div>

                {userInfo?.is_accepted == '0' &&
                    <>
                        <Tooltip label='Please do not print from here. Use it only to see the preview'>
                            <Button colorScheme='facebook' size='sm' onClick={handlePreview}>
                                <span>Preview Id Card</span>
                            </Button>
                        </Tooltip>
                        <Tooltip label='Header Hide/Show'>
                            <Button colorScheme='linkedin' size='sm' onClick={() => setShowHeader(!showHeader)}>
                                <span>Header {showHeader ? 'Hide' : 'Show'}</span>
                            </Button>
                        </Tooltip>
                        {(userInfo?.user_type === '5' || userInfo?.user_type === '8' || userInfo?.user_type === '4') &&
                            <Tooltip label='Validity update of this student. A popup will appear to change the validity.'>
                                <Button colorScheme='orange' size='sm' onClick={updateValidity}>
                                    <span>Change Validity</span>
                                </Button>
                            </Tooltip>
                        }
                    </>
                }
            </Flex>
        )
    }

    return (
        <>
            <Center>
                <Heading as='h2' size='xl' color={"teal"}>
                    User Details ({getType(userInfo?.user_type)})
                </Heading>
            </Center>

            <VStack spacing={4} justifyContent="center" mt={3}>
                <Text fontWeight="bold">RF Card Number: {userInfo.id_card_no ? userInfo.id_card_no : 'Not set yet'}</Text>
                <Button
                    colorScheme="blue"
                    size={'sm'}
                    onClick={() => {
                        onOpen();
                    }}
                >
                    Set Id Card No
                </Button>
            </VStack>

            <UpdateRfidCardNo
                isOpen={isOpen}
                onClose={onClose}
                user_id={userInfo.id}
                id_card_no={userInfo.id_card_no}
                fullname={userInfo.fullname}
                mobile={userInfo.mobile}
                sex={userInfo.sex}
                nid={userInfo.nid}
                batch={studentInfo?.hons_masters_details?.batch || studentInfo?.mphil_phd_details?.session || studentInfo?.evening_course_student_details?.admission_year}
                department_id={studentInfo?.hons_masters_details?.department_id || studentInfo?.mphil_phd_details?.department_id || studentInfo?.evening_course_student_details?.department_id}
                institute_id={studentInfo?.hons_masters_details?.institute_id || studentInfo?.mphil_phd_details?.institute_id || studentInfo?.evening_course_student_details?.institute_id}
                user_type={userInfo?.user_type}
                designation_id={employeeInfo?.employee_details?.designation_id}
                office_id={employeeInfo?.employee_details?.office_id}
                apply_id={apply_id}
                setRefresh={setRefresh}
            />

            <UserInfo userInfo={userInfo} apply_id={apply_id} />

            <Center>
                <Heading as='h2' size='xl' color={"teal"}>
                    {isEmployee(userInfo?.user_type) ? 'Employee Info' : 'Student Info'}
                </Heading>
            </Center>
            <div className={UserInfoStyle.details_div}>
                <div className={UserInfoStyle.others}>
                    {/* Id Card */}
                    <Center>
                        <IdCard />
                    </Center>
                </div>
                <div className={UserInfoStyle.info}>
                    {isEmployee(userInfo?.user_type)
                        ? <EmployeeInfo employeeInfo={employeeInfo} />
                        : <StudentInfo studentInfo={studentInfo} />
                    }
                </div>
            </div>

        </>
    )
}
